import { Screen, ScreenConfig } from '../core/Screen';
import { APIManager, StateManager, ScreenRouter } from '../../managers';
import { screenHeaderHTML, renderScreenCurrencies } from '../components/ScreenHeader';
import { CB, getBgUrl, getFullSpriteUrl } from '../../combat/spriteConfig';
import type { PlayerData, CampaignData, PatternStashItem, PatternAffixData, CrawlRunState, CombatActionResult, PresenceData } from '../../types';

// Affix display formatters
const PATTERN_AFFIX_INFO: Record<string, { name: string; format: (a: PatternAffixData) => string }> = {
  reinforced: { name: 'Reinforced', format: a => `+${a.dangerValue}% Monster Life` },
  savage: { name: 'Savage', format: a => `+${a.dangerValue}% Monster Damage` },
  swift: { name: 'Swift', format: a => `+${a.dangerValue}% Monster Speed` },
  smothered: { name: 'Smothered', format: a => `−${a.dangerValue}% Player Healing` },
  sprawling: { name: 'Sprawling', format: () => `+1 Encounter` },
};

type ActiveTab = 'campaign' | 'patterns';
type PatternRarityFilter = 'all' | 'normal' | 'magic' | 'rare';
type PatternSort = 'newest' | 'level' | 'affix-count';

const RARITY_ORDER: Record<string, number> = { rare: 3, magic: 2, normal: 1 };

export class CrawlsBrowserScreen extends Screen {
  readonly screenConfig: ScreenConfig = {
    id: 'crawls',
    name: 'Campaign',
    persistent: false,
    needsPhaser: false,
  };

  /** Patterns unlock after Act 3 completion (step 15). */
  private static readonly PATTERNS_UNLOCK_STEP = 15;

  private activeTab: ActiveTab = 'campaign';
  private userPickedTab = false;
  private patterns: PatternStashItem[] = [];
  private selectedPatternId: string | null = null;
  private rarityFilter: PatternRarityFilter = 'all';
  private sortMode: PatternSort = 'newest';

  constructor() {
    super({ rootId: 'crawls-browser-root' });
  }

  createDOM(container: HTMLElement): void {
    // Decide default tab SYNCHRONOUSLY before rendering — no flash.
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const step = player?.campaignStep ?? 0;
    const patternsUnlocked = step >= CrawlsBrowserScreen.PATTERNS_UNLOCK_STEP;
    this.activeTab = patternsUnlocked ? 'patterns' : 'campaign';

    const patternsActive = this.activeTab === 'patterns';
    const root = document.createElement('div');
    root.id = 'crawls-browser-root';
    root.innerHTML = `
      <div class="crawls-browser hub-screen">
        <div class="hub-screen-bg" style="background-image:url('/activity/sprites/processed/hub_bg/irremar_archive.webp')"></div>
        <div class="hub-screen-vignette"></div>
        ${screenHeaderHTML('Campaign')}

        <!-- Tab bar: hidden until Patterns unlocks -->
        <div class="crawls-tabbar" role="tablist" style="${patternsUnlocked ? '' : 'display:none'}">
          <button class="crawls-tab crawls-tab--campaign ${patternsActive ? '' : 'active'}" data-tab="campaign" type="button">Campaign</button>
          <button class="crawls-tab crawls-tab--patterns ${patternsActive ? 'active' : ''}" data-tab="patterns" type="button">
            Patterns <span class="crawls-tab-badge"></span>
          </button>
        </div>

        <!-- Campaign tab -->
        <div class="crawls-tab-panel crawls-tab-panel--campaign" style="${patternsActive ? 'display:none' : ''}">
          <div class="campaign-content"></div>
        </div>

        <!-- Patterns tab -->
        <div class="crawls-tab-panel crawls-tab-panel--patterns" style="${patternsActive ? '' : 'display:none'}">
          <div class="patterns-toolbar">
            <div class="patterns-filters" role="tablist">
              <button class="patterns-filter active" data-filter="all" type="button">All</button>
              <button class="patterns-filter" data-filter="rare" type="button">Rare</button>
              <button class="patterns-filter" data-filter="magic" type="button">Magic</button>
              <button class="patterns-filter" data-filter="normal" type="button">Normal</button>
            </div>
            <div class="patterns-sort">
              <label class="patterns-sort-label">Sort</label>
              <select class="patterns-sort-select">
                <option value="newest">Newest</option>
                <option value="level">Level</option>
                <option value="affix-count">Most Mods</option>
              </select>
            </div>
          </div>
          <div class="patterns-layout">
            <div class="patterns-stash"></div>
            <div class="patterns-detail"></div>
          </div>
        </div>
      </div>
    `;
    container.appendChild(root);
  }

  protected cacheElements(): void {}

  protected bindEvents(): void {
    // Tab switching
    this.rootEl?.querySelectorAll('.crawls-tab').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const tab = (e.currentTarget as HTMLElement).dataset.tab as ActiveTab;
        if (tab) {
          this.userPickedTab = true;
          this.switchTab(tab);
        }
      });
    });

    // Pattern filter pills
    this.rootEl?.querySelectorAll('.patterns-filter').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const filter = (e.currentTarget as HTMLElement).dataset.filter as PatternRarityFilter;
        if (filter) {
          this.rarityFilter = filter;
          this.rootEl?.querySelectorAll('.patterns-filter').forEach(b => b.classList.toggle('active', b === e.currentTarget));
          this.renderPatternStash();
        }
      });
    });

    // Sort dropdown
    const sortSelect = this.$('.patterns-sort-select') as HTMLSelectElement | null;
    sortSelect?.addEventListener('change', () => {
      this.sortMode = sortSelect.value as PatternSort;
      this.renderPatternStash();
    });
  }

  protected setupSubscriptions(): void {}

  async onEnter(): Promise<void> {
    this.onRender();
    this.renderCampaign();
    await this.loadPatterns();
  }

  protected onRender(): void {
    if (this.rootEl) renderScreenCurrencies(this.rootEl, 'crawls-browser');
  }

  // ─── Tab management ────────────────────────────────────────

  private switchTab(tab: ActiveTab): void {
    this.activeTab = tab;
    this.rootEl?.querySelectorAll('.crawls-tab').forEach(btn => {
      btn.classList.toggle('active', (btn as HTMLElement).dataset.tab === tab);
    });
    const campaignPanel = this.$('.crawls-tab-panel--campaign') as HTMLElement | null;
    const patternsPanel = this.$('.crawls-tab-panel--patterns') as HTMLElement | null;
    if (campaignPanel) campaignPanel.style.display = tab === 'campaign' ? '' : 'none';
    if (patternsPanel) patternsPanel.style.display = tab === 'patterns' ? '' : 'none';
  }

  // ─── Campaign tab ──────────────────────────────────────────

  private renderCampaign(): void {
    const campaignData = StateManager.get('campaignData') as CampaignData | undefined;
    const container = this.$('.campaign-content');
    if (!container || !campaignData) return;

    const ACT_BOUNDARIES = [
      { start: 0,  end: 5,  label: 'Act 1', subtitle: 'The Flight' },
      { start: 5,  end: 10, label: 'Act 2', subtitle: 'The Quiet Trade' },
      { start: 10, end: 15, label: 'Act 3', subtitle: 'The Pale Watch' },
    ];

    const steps = campaignData.steps;
    const completedSteps = steps.filter(s => s.status === 'completed');

    if (completedSteps.length === 0) {
      container.innerHTML = `
        <div class="campaign-empty">
          <div class="campaign-empty-title">No Chapters Completed</div>
          <div class="campaign-empty-desc">Complete chapters from the home screen to replay them here.</div>
        </div>
      `;
      return;
    }

    const stepByIndex = new Map<number, CampaignData['steps'][number]>();
    for (const s of steps) stepByIndex.set(s.stepIndex, s);

    // Only render acts the player has reached — an act shows once it has any
    // non-locked step (available or completed). Future acts are hidden entirely.
    const visibleActs = ACT_BOUNDARIES.filter(act => {
      for (let i = act.start; i < act.end; i++) {
        const step = stepByIndex.get(i);
        if (step && step.status !== 'locked') return true;
      }
      return false;
    });

    const actSections = visibleActs.map(act => {
      const cards: string[] = [];
      for (let i = act.start; i < act.end; i++) {
        const step = stepByIndex.get(i);
        const chapterNum = i + 1;
        if (step && step.status === 'completed') {
          const bgPath = getBgUrl(step.bgKey);
          const spriteUrl = step.previewSpriteKey ? getFullSpriteUrl(step.previewSpriteKey) : '';
          const spriteHTML = spriteUrl
            ? `<img class="chapter-card-sprite" src="${spriteUrl}" alt="" loading="lazy" onerror="this.style.display='none'" />`
            : '';
          cards.push(`
            <button class="chapter-card chapter-card--replayable" data-crawl-id="${step.crawlId}" type="button">
              <div class="chapter-card-art">
                <img class="chapter-card-bg" src="${bgPath}" alt="" loading="lazy" onerror="this.style.display='none'" />
                <div class="chapter-card-vignette"></div>
                ${spriteHTML}
                <div class="chapter-card-grad"></div>
              </div>
              <div class="chapter-card-badge">Ch. ${chapterNum}</div>
              <div class="chapter-card-body">
                <div class="chapter-card-title">${step.title}</div>
                <div class="chapter-card-meta">Lv ${step.dropItemLevel} · ${step.encounterCount} Rooms</div>
              </div>
              <div class="chapter-card-replay">Replay</div>
            </button>
          `);
        } else {
          cards.push(`
            <div class="chapter-card chapter-card--locked" aria-disabled="true">
              <div class="chapter-card-badge">Ch. ${chapterNum}</div>
              <div class="chapter-card-lock">\u{1F512}</div>
            </div>
          `);
        }
      }
      return `
        <div class="campaign-act">
          <div class="campaign-act-header">
            <span class="campaign-act-label">${act.label}</span>
            <span class="campaign-act-subtitle">${act.subtitle}</span>
          </div>
          <div class="campaign-act-grid">${cards.join('')}</div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="campaign-replay-view hub-screen-body">
        <div class="campaign-replay-header">Story So Far</div>
        ${actSections}
      </div>
    `;

    container.querySelectorAll('.chapter-card--replayable').forEach(card => {
      card.addEventListener('click', async (e) => {
        const cardEl = e.currentTarget as HTMLButtonElement;
        const crawlId = cardEl.dataset.crawlId;
        if (!crawlId || cardEl.disabled) return;

        cardEl.disabled = true;
        cardEl.classList.add('chapter-card--loading');

        const result = await APIManager.getCrawlDetails(crawlId);
        if (!result.success || !result.data) {
          cardEl.disabled = false;
          cardEl.classList.remove('chapter-card--loading');
          return;
        }

        ScreenRouter.navigate('hero-select', {
          crawlId,
          crawlType: 'campaign_replay',
          crawlDetails: result.data,
        });
      });
    });

    this.loadSocialAnnotations(steps);
  }

  private async loadSocialAnnotations(steps: CampaignData['steps']): Promise<void> {
    const presence = StateManager.get('presenceData') as PresenceData | undefined;
    if (!presence || presence.participants.length <= 1) return;

    const selfId = presence.self?.id;
    const otherIds = presence.participants.filter(p => p.id !== selfId).map(p => p.id);
    if (otherIds.length === 0) return;

    for (const step of steps) {
      if (step.status === 'locked') continue;

      const result = await APIManager.getCampaignCompletions(otherIds, step.stepIndex);
      if (!result.success || !result.data || result.data.completedIds.length === 0) continue;

      const clearedIds = result.data.completedIds;
      const names = clearedIds.map(id => {
        const p = presence.participants.find(pp => pp.id === id);
        return p?.global_name || p?.username || 'Someone';
      });

      const badgeText = names.length === 1
        ? `${names[0]} cleared this`
        : `${names.length} friends cleared this`;

      // Find the matching card by crawl ID
      const card = this.rootEl?.querySelector(`.chapter-card--replayable[data-crawl-id="${step.crawlId}"]`);
      const bodyEl = card?.querySelector('.chapter-card-body');
      if (bodyEl && !bodyEl.querySelector('.campaign-social-badge')) {
        const badge = document.createElement('span');
        badge.className = 'campaign-social-badge';
        badge.textContent = badgeText;
        bodyEl.appendChild(badge);
        continue;
      }

    }
  }

  // ─── Patterns tab ──────────────────────────────────────────

  private async loadPatterns(): Promise<void> {
    const player = StateManager.get('playerData') as PlayerData | undefined;
    const step = player?.campaignStep ?? 0;

    // Show tab bar + patterns tab only when unlocked
    const tabBar = this.$('.crawls-tabbar') as HTMLElement | null;
    if (step < CrawlsBrowserScreen.PATTERNS_UNLOCK_STEP) {
      if (tabBar) tabBar.style.display = 'none';
      return;
    }
    if (tabBar) tabBar.style.display = '';

    const result = await APIManager.getPatterns();
    if (!result.success || !result.data) {
      this.patterns = [];
    } else {
      this.patterns = result.data.patterns;
    }

    this.updatePatternBadge();
    this.renderPatternStash();
  }

  private updatePatternBadge(): void {
    const badge = this.$('.crawls-tab-badge') as HTMLElement | null;
    if (!badge) return;
    if (this.patterns.length === 0) {
      badge.textContent = '';
      badge.style.display = 'none';
    } else {
      badge.textContent = String(this.patterns.length);
      badge.style.display = '';
    }
  }

  private getFilteredSortedPatterns(): PatternStashItem[] {
    let filtered = this.patterns;
    if (this.rarityFilter !== 'all') {
      filtered = filtered.filter(p => p.rarity === this.rarityFilter);
    }
    const sorted = [...filtered];
    if (this.sortMode === 'level') {
      sorted.sort((a, b) => b.enemyLevel - a.enemyLevel);
    } else if (this.sortMode === 'affix-count') {
      sorted.sort((a, b) => b.affixes.length - a.affixes.length || (RARITY_ORDER[b.rarity] ?? 0) - (RARITY_ORDER[a.rarity] ?? 0));
    }
    // 'newest' = preserve API order (already DESC by createdAt)
    return sorted;
  }

  private renderPatternStash(): void {
    const stashEl = this.$('.patterns-stash');
    const detailEl = this.$('.patterns-detail');
    if (!stashEl || !detailEl) return;

    const list = this.getFilteredSortedPatterns();

    if (list.length === 0) {
      stashEl.innerHTML = this.renderPatternEmptyState();
      detailEl.innerHTML = '';
      return;
    }

    // If selected pattern was filtered out, clear selection
    if (this.selectedPatternId && !list.find(p => p.id === this.selectedPatternId)) {
      this.selectedPatternId = null;
    }
    // Auto-select first if nothing selected
    if (!this.selectedPatternId) {
      this.selectedPatternId = list[0].id;
    }

    stashEl.innerHTML = list.map(p => this.renderPatternCard(p)).join('');

    stashEl.querySelectorAll('.pattern-card').forEach(card => {
      card.addEventListener('click', () => {
        const id = (card as HTMLElement).dataset.patternId;
        if (id) {
          this.selectedPatternId = id;
          stashEl.querySelectorAll('.pattern-card').forEach(c => c.classList.toggle('selected', c === card));
          this.renderPatternDetail();
        }
      });
    });

    this.renderPatternDetail();
  }

  private renderPatternCard(p: PatternStashItem): string {
    const imgPath = `${import.meta.env.BASE_URL}sprites/processed/bg/${p.bgKey}.webp${CB}`;
    const isSelected = p.id === this.selectedPatternId;
    return `
      <div class="pattern-card pattern-card--${p.rarity} ${isSelected ? 'selected' : ''}" data-pattern-id="${p.id}">
        <div class="pattern-card-thumb">
          <img src="${imgPath}" alt="" loading="lazy" onerror="this.style.display='none'" />
          <div class="pattern-card-thumb-grad"></div>
        </div>
        <div class="pattern-card-body">
          <div class="pattern-card-name">${p.typeName}</div>
          <div class="pattern-card-meta">
            <span>Lv ${p.enemyLevel}</span>
            <span class="pattern-card-dot">·</span>
            <span>${p.affixes.length} mod${p.affixes.length === 1 ? '' : 's'}</span>
          </div>
        </div>
      </div>
    `;
  }

  private renderPatternEmptyState(): string {
    return `
      <div class="patterns-empty">
        <div class="patterns-empty-title">No Patterns Yet</div>
        <div class="patterns-empty-desc">Patterns drop from completed campaign chapters and other Patterns. Each one is a unique endgame map with rolled modifiers.</div>
      </div>
    `;
  }

  private renderPatternDetail(): void {
    const detailEl = this.$('.patterns-detail');
    if (!detailEl) return;

    const p = this.patterns.find(x => x.id === this.selectedPatternId);
    if (!p) {
      detailEl.innerHTML = '';
      return;
    }

    const imgPath = `${import.meta.env.BASE_URL}sprites/processed/bg/${p.bgKey}.webp${CB}`;

    const affixLines = p.affixes.length === 0
      ? '<div class="pattern-detail-no-affixes">No modifiers</div>'
      : p.affixes.map(a => {
          const info = PATTERN_AFFIX_INFO[a.id];
          const label = info ? info.format(a) : a.id;
          const rewards: string[] = [];
          if (a.quantity) rewards.push(`+${a.quantity}% Quantity`);
          if (a.rarity) rewards.push(`+${a.rarity}% Rarity`);
          if (a.packSize) rewards.push(`+${a.packSize} Pack Size`);
          return `
            <div class="pattern-detail-affix">
              <div class="pattern-detail-danger">${label}</div>
              ${rewards.length ? `<div class="pattern-detail-reward">${rewards.join(' · ')}</div>` : ''}
            </div>
          `;
        }).join('');

    // Craft buttons based on current rarity
    let craftHTML = '';
    if (p.rarity === 'normal') {
      craftHTML = `<button class="pattern-craft-btn" type="button" data-pattern-id="${p.id}" data-action="upgrade">
        <span class="pattern-craft-label">Upgrade to Magic</span>
        <span class="pattern-craft-cost">1 Lustrous Yarn</span>
      </button>`;
    } else if (p.rarity === 'magic') {
      craftHTML = `<button class="pattern-craft-btn" type="button" data-pattern-id="${p.id}" data-action="upgrade">
        <span class="pattern-craft-label">Upgrade to Rare</span>
        <span class="pattern-craft-cost">1 Gilded Yarn</span>
      </button>`;
    }

    detailEl.innerHTML = `
      <div class="pattern-detail-banner">
        <img src="${imgPath}" alt="" loading="lazy" onerror="this.style.display='none'" />
        <div class="pattern-detail-banner-grad"></div>
        <div class="pattern-detail-banner-text">
          <div class="pattern-detail-rarity pattern-detail-rarity--${p.rarity}">${p.rarity}</div>
          <div class="pattern-detail-name pattern-detail-name--${p.rarity}">${p.typeName}</div>
          <div class="pattern-detail-meta">
            <span>Lv ${p.enemyLevel}</span>
            <span class="pattern-detail-dot">·</span>
            <span>${p.rooms[0]}–${p.rooms[1]} Rooms</span>
          </div>
        </div>
      </div>
      <div class="pattern-detail-description">${p.typeDescription}</div>
      <div class="pattern-detail-section-label">Modifiers</div>
      <div class="pattern-detail-affixes">${affixLines}</div>
      ${craftHTML ? `<div class="pattern-detail-section-label">Craft</div><div class="pattern-detail-craft">${craftHTML}</div>` : ''}
      <div class="pattern-detail-actions">
        <button class="pattern-detail-run-btn" type="button" data-pattern-id="${p.id}">Run Pattern</button>
        <button class="pattern-detail-delete-btn" type="button" data-pattern-id="${p.id}">Delete</button>
      </div>
    `;

    detailEl.querySelector('.pattern-detail-run-btn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.patternId;
      if (id) this.runPattern(id, btn);
    });

    detailEl.querySelector('.pattern-detail-delete-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.patternId;
      if (!id) return;
      if (!confirm('Delete this Pattern? This cannot be undone.')) return;
      await APIManager.deletePattern(id);
      this.selectedPatternId = null;
      await this.loadPatterns();
    });

    detailEl.querySelector('.pattern-craft-btn')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      const id = btn.dataset.patternId;
      if (!id) return;
      btn.disabled = true;
      const result = await APIManager.upgradePattern(id);
      if (!result.success) {
        console.error('[CrawlsBrowser] Failed to upgrade pattern:', result.error);
        btn.disabled = false;
        return;
      }
      // Reload patterns and refresh currencies (the upgrade spent yarn)
      await this.loadPatterns();
      // Refresh player state for currency display
      const stateResult = await APIManager.fetchUnifiedState();
      if (stateResult.success && stateResult.data) {
        StateManager.set('playerData', stateResult.data.playerData);
      }
    });
  }

  private async runPattern(patternId: string, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.textContent = 'Loading...';

    const result = await APIManager.runPattern(patternId);
    if (!result.success || !result.data) {
      console.error('[CrawlsBrowser] Failed to run pattern:', result.error);
      btn.disabled = false;
      btn.textContent = 'Run Pattern';
      return;
    }

    const data = result.data as { run: CrawlRunState; combatResult: CombatActionResult };
    const { run, combatResult } = data;

    // Use the cached pattern object so we have name + bgKey
    const pattern = this.patterns.find(p => p.id === patternId);

    const patternConfig = {
      id: run.crawlConfigId,
      name: pattern?.typeName ?? 'Pattern',
      encounters: run.encounters.map((eid: string) => ({ tier: 'standard' as const, encounterId: eid })),
      completionBonus: { xp: 0, gold: 0 },
      dropItemLevel: pattern?.enemyLevel ?? 35,
      bgKey: pattern?.bgKey ?? 'skarn_cave',
      lightingType: 'daylight' as const,
    };
    StateManager.set('crawlData', { config: patternConfig, run } as any);

    ScreenRouter.replace('combat', { combatResult, run });
  }
}
